// Tela de Aniversários (/birthday-settings) — mockup "Tela Aeros Dark -
// Aniversários": header 60px com a pill do próximo envio, coluna esquerda com
// os dois cards recolhíveis (contatos e equipe) e coluna direita com a
// pré-visualização do WhatsApp, os próximos aniversários e os KPIs.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import SaveIcon from "@material-ui/icons/Save";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ContactsIcon from "@material-ui/icons/Contacts";
import GroupIcon from "@material-ui/icons/Group";
import CampaignIcon from "@mui/icons-material/Campaign";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SendIcon from "@material-ui/icons/Send";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import BadgeIcon from "@material-ui/icons/AssignmentInd";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useCampaignPageStyles from "../../pages/Campaigns/campaignPageStyles";
import useBirthdayPageStyles from "../../pages/BirthdaySettings/birthdayPageStyles";
import TestMessageModal from "./TestMessageModal";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { useTheme } from "@material-ui/core/styles";
import {
  CONTACT_VARIABLES,
  USER_VARIABLES,
  resolveMessage,
  insertVariableAt,
  initials,
  avatarColor,
  trimSeconds,
  nextSendLabel,
  dayMonthParts,
  upcomingStatus,
  ageLabel,
} from "../../pages/BirthdaySettings/birthdayRules";

// Idade do contato fictício da pré-visualização, usada só quando não há
// nenhum aniversariante real hoje para ilustrar a mensagem.
const SAMPLE_AGE = 30;

const BirthdaySettings = () => {
  const classes = useBirthdayPageStyles();
  const c = useCampaignPageStyles({ grid: "1fr" });
  const theme = useTheme();
  const history = useHistory();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ contact: true, user: true });
  const [whatsapps, setWhatsapps] = useState([]);
  const [today, setToday] = useState({ users: [], contacts: [] });
  const [upcoming, setUpcoming] = useState([]);
  const [stats, setStats] = useState(null);
  const [testOpen, setTestOpen] = useState(false);

  const contactMessageRef = useRef(null);
  const userMessageRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get("/birthdays/settings");
        setSettings(data?.data || data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    async function loadConnections() {
      try {
        const { data } = await api.get("/whatsapp");
        setWhatsapps(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    }
    loadConnections();
  }, []);

  useEffect(() => {
    async function loadToday() {
      try {
        const { data } = await api.get("/birthdays/today");
        setToday(data?.data || { users: [], contacts: [] });
      } catch (err) {
        // Silencioso: a pill do cabeçalho e a linha "enviado hoje" somem, mas
        // as configurações — o que a tela existe para editar — continuam.
        setToday({ users: [], contacts: [] });
      }
    }
    loadToday();
  }, []);

  const loadUpcoming = useCallback(async () => {
    try {
      const { data } = await api.get("/birthdays/upcoming", { params: { limit: 5 } });
      setUpcoming(data?.data?.items || []);
    } catch (err) {
      setUpcoming([]);
    }
  }, []);

  useEffect(() => {
    loadUpcoming();
  }, [loadUpcoming]);

  useEffect(() => {
    async function loadStats() {
      try {
        const { data } = await api.get("/birthdays/stats");
        setStats(data?.data || null);
      } catch (err) {
        setStats(null);
      }
    }
    loadStats();
  }, []);

  const change = (field, value) =>
    setSettings((atual) => ({ ...atual, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/birthdays/settings", settings);
      toast.success(i18n.t("aeros.birthday.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // Insere a variável onde o cursor está e devolve o foco para o campo, com o
  // cursor logo depois do texto inserido.
  const insertVariable = (field, ref, variable) => {
    const input = ref.current;
    const texto = settings?.[field] || "";
    const { text, cursor } = insertVariableAt(
      texto,
      variable,
      input?.selectionStart,
      input?.selectionEnd
    );
    change(field, text);
    requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      input.setSelectionRange(cursor, cursor);
    });
  };

  const sentTodayCount = useMemo(
    () => (today.contacts || []).filter((contato) => contato.messageSent).length,
    [today.contacts]
  );

  const selectedWhatsapp = whatsapps.find(
    (w) => w.id === Number(settings?.whatsappId)
  );

  // Alvo da pré-visualização: o primeiro aniversariante real de hoje, para a
  // mensagem aparecer com dados de verdade; sem ninguém, um contato fictício.
  const previewTarget = (today.contacts || [])[0] || {
    name: i18n.t("aeros.birthday.preview.sampleName"),
    age: SAMPLE_AGE,
  };
  const previewText = resolveMessage(settings?.contactBirthdayMessage, {
    name: previewTarget.name,
    age: previewTarget.age,
  });

  const renderSwitch = (field, extraProps = {}) => (
    <Switch
      classes={{ root: classes.switchRoot }}
      checked={!!settings?.[field]}
      onChange={(e) => change(field, e.target.checked)}
      {...extraProps}
    />
  );

  const renderCardHeader = ({ tileClass, Icon, title, subtitle, field, section }) => {
    const ativo = !!settings?.[field];
    return (
      <div className={classes.cardHeader}>
        <span className={`${classes.tile} ${tileClass}`}>
          <Icon />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.cardTitle}>{title}</div>
          <div className={classes.cardSubtitle}>{subtitle}</div>
        </div>
        <span
          className={`${classes.toggleGroup} ${
            ativo ? classes.toggleOn : classes.toggleOff
          }`}
        >
          {i18n.t(ativo ? "aeros.birthday.active" : "aeros.birthday.inactive")}
          {renderSwitch(field)}
        </span>
        <Tooltip
          title={i18n.t(
            expanded[section] ? "aeros.birthday.collapse" : "aeros.birthday.expand"
          )}
        >
          <IconButton
            className={classes.expandButton}
            onClick={() =>
              setExpanded((atual) => ({ ...atual, [section]: !atual[section] }))
            }
            aria-label={i18n.t(
              expanded[section] ? "aeros.birthday.collapse" : "aeros.birthday.expand"
            )}
          >
            {expanded[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  const renderVariableChips = (variables, field, ref) => (
    <span className={classes.varChips}>
      {variables.map((variable) => (
        <Tooltip
          key={variable}
          title={i18n.t("aeros.birthday.insertVariable", { variable })}
        >
          <Button
            className={classes.varChip}
            onClick={() => insertVariable(field, ref, variable)}
          >
            {variable}
          </Button>
        </Tooltip>
      ))}
    </span>
  );

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={20} />
        {i18n.t("aeros.birthday.loading")}
      </div>
    );
  }

  return (
    <>
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>{i18n.t("aeros.birthday.title")}</div>
          <div className={classes.pageSubtitle}>{i18n.t("aeros.birthday.subtitle")}</div>
        </div>
        <div className={c.headerActions}>
          <span className={classes.nextSendPill}>
            <ScheduleIcon />
            {nextSendLabel({
              time: settings?.sendBirthdayTime,
              todayCount: (today.contacts || []).length,
              enabled: !!settings?.contactBirthdayEnabled,
            })}
          </span>
          <Button
            className={c.gradientButton}
            startIcon={saving ? undefined : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={18} /> : i18n.t("aeros.birthday.save")}
          </Button>
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.column}>
          {/* ---- Aniversários de contatos ---- */}
          <div className={classes.card}>
            {renderCardHeader({
              tileClass: classes.tileCyan,
              Icon: ContactsIcon,
              title: i18n.t("aeros.birthday.contacts.title"),
              subtitle: i18n.t("aeros.birthday.contacts.subtitle"),
              field: "contactBirthdayEnabled",
              section: "contact",
            })}
            <Collapse in={expanded.contact}>
              <div className={classes.cardBody}>
                <div className={classes.fieldRow}>
                  <div>
                    <div className={classes.fieldLabel}>
                      {i18n.t("aeros.birthday.sendTime")}
                    </div>
                    <TextField
                      type="time"
                      value={trimSeconds(settings?.sendBirthdayTime)}
                      onChange={(e) => change("sendBirthdayTime", e.target.value)}
                      variant="outlined"
                      fullWidth
                      className={`${classes.input} ${classes.inputTime}`}
                      inputProps={{ "aria-label": i18n.t("aeros.birthday.sendTime") }}
                    />
                    <div className={classes.helper}>
                      {i18n.t("aeros.birthday.sendTimeHelper")}
                    </div>
                  </div>
                  <div>
                    <div className={classes.fieldLabel}>
                      {i18n.t("aeros.birthday.connection")}
                    </div>
                    <TextField
                      select
                      value={settings?.whatsappId || ""}
                      onChange={(e) =>
                        change("whatsappId", e.target.value ? Number(e.target.value) : null)
                      }
                      variant="outlined"
                      fullWidth
                      className={classes.input}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => {
                          const conn = whatsapps.find((w) => w.id === Number(value));
                          if (!conn) return i18n.t("aeros.birthday.connectionNone");
                          const online = conn.status === "CONNECTED";
                          return (
                            <span className={classes.connOption}>
                              <span
                                className={classes.connDot}
                                style={{
                                  backgroundColor: online ? AEROS.success : AEROS.error,
                                }}
                              />
                              {conn.name}
                              {conn.number ? ` · ${conn.number}` : ""}
                              <span
                                className={`${classes.connTag} ${
                                  online ? classes.connTagOn : classes.connTagOff
                                }`}
                              >
                                {i18n.t(
                                  online
                                    ? "aeros.birthday.connected"
                                    : "aeros.birthday.disconnected"
                                )}
                              </span>
                            </span>
                          );
                        },
                      }}
                    >
                      <MenuItem value="">
                        {i18n.t("aeros.birthday.connectionNone")}
                      </MenuItem>
                      {whatsapps.map((conn) => (
                        <MenuItem key={conn.id} value={conn.id}>
                          {conn.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                </div>

                <div>
                  <div className={classes.messageLabelRow}>
                    <span className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                      {i18n.t("aeros.birthday.contactMessage")}
                    </span>
                    {renderVariableChips(
                      CONTACT_VARIABLES,
                      "contactBirthdayMessage",
                      contactMessageRef
                    )}
                  </div>
                  <TextField
                    value={settings?.contactBirthdayMessage || ""}
                    onChange={(e) => change("contactBirthdayMessage", e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={4}
                    className={`${classes.input} ${classes.inputMultiline}`}
                    inputRef={contactMessageRef}
                    inputProps={{
                      "aria-label": i18n.t("aeros.birthday.contactMessage"),
                    }}
                  />
                  <div className={classes.helper}>
                    {i18n.t("aeros.birthday.variableHint")}
                  </div>
                </div>

                <div className={classes.statusRow}>
                  {sentTodayCount > 0 && (
                    <span className={classes.sentToday}>
                      <CheckCircleIcon />
                      {i18n.t(
                        sentTodayCount === 1
                          ? "aeros.birthday.sentTodayOne"
                          : "aeros.birthday.sentToday",
                        { count: sentTodayCount }
                      )}
                    </span>
                  )}
                  <Button
                    className={classes.testButton}
                    startIcon={<SendIcon />}
                    onClick={() => setTestOpen(true)}
                  >
                    {i18n.t("aeros.birthday.testMessage")}
                  </Button>
                </div>
              </div>
            </Collapse>
          </div>

          {/* ---- Aniversários da equipe ---- */}
          <div className={classes.card}>
            {renderCardHeader({
              tileClass: classes.tilePurple,
              Icon: GroupIcon,
              title: i18n.t("aeros.birthday.users.title"),
              subtitle: i18n.t("aeros.birthday.users.subtitle"),
              field: "userBirthdayEnabled",
              section: "user",
            })}
            <Collapse in={expanded.user}>
              <div className={classes.cardBody}>
                <div className={classes.optionRow}>
                  <CampaignIcon />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.optionTitle}>
                      {i18n.t("aeros.birthday.announcement.title")}
                    </div>
                    <div className={classes.optionDesc}>
                      {i18n.t("aeros.birthday.announcement.desc")}
                    </div>
                  </div>
                  {renderSwitch("createAnnouncementForUsers", {
                    disabled: !settings?.userBirthdayEnabled,
                  })}
                </div>

                <div>
                  <div className={classes.messageLabelRow}>
                    <span className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                      {i18n.t("aeros.birthday.userMessage")}
                    </span>
                    {renderVariableChips(
                      USER_VARIABLES,
                      "userBirthdayMessage",
                      userMessageRef
                    )}
                  </div>
                  <TextField
                    value={settings?.userBirthdayMessage || ""}
                    onChange={(e) => change("userBirthdayMessage", e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={3}
                    className={`${classes.input} ${classes.inputMultiline}`}
                    inputRef={userMessageRef}
                    inputProps={{ "aria-label": i18n.t("aeros.birthday.userMessage") }}
                  />
                </div>
              </div>
            </Collapse>
          </div>
        </div>

        {/* ---- coluna direita ---- */}
        <div className={classes.column}>
          <div className={classes.sideCard}>
            <div className={classes.eyebrow}>{i18n.t("aeros.birthday.preview.title")}</div>
            <div className={classes.chatFrame}>
              <div className={classes.chatHead}>
                <span
                  className={classes.chatAvatar}
                  style={{ backgroundColor: avatarColor(previewTarget.id || 0) }}
                >
                  {initials(previewTarget.name)}
                </span>
                <span className={classes.chatName}>{previewTarget.name}</span>
                <span className={classes.chatMeta}>
                  {selectedWhatsapp?.name || i18n.t("aeros.birthday.connectionNone")}
                </span>
              </div>
              {previewText ? (
                <div className={classes.bubble}>
                  {previewText}
                  <div className={classes.bubbleTime}>
                    {trimSeconds(settings?.sendBirthdayTime)} ✓✓
                  </div>
                </div>
              ) : (
                <div className={classes.bubbleEmpty}>
                  {i18n.t("aeros.birthday.preview.empty")}
                </div>
              )}
            </div>
            <div className={classes.note}>
              <InfoOutlinedIcon />
              {i18n.t("aeros.birthday.preview.note")}
            </div>
          </div>

          <div className={classes.sideCard}>
            <div className={classes.eyebrowRow}>
              <span className={classes.eyebrow}>
                {i18n.t("aeros.birthday.upcoming.title")}
              </span>
              <Button
                className={classes.linkButton}
                onClick={() => history.push("/contacts")}
              >
                {i18n.t("aeros.birthday.upcoming.seeAll")}
              </Button>
            </div>
            {upcoming.length === 0 ? (
              <div className={classes.emptyLine}>
                {i18n.t("aeros.birthday.upcoming.empty")}
              </div>
            ) : (
              upcoming.map((item) => {
                const { day, month } = dayMonthParts(item.birthDate);
                const status = upcomingStatus(item);
                const tone =
                  status === "sent"
                    ? stateText(theme, AEROS.success, "success")
                    : status === "today"
                    ? stateText(theme, AEROS.cyan, "info")
                    : theme.palette.text.secondary;
                const toneBg =
                  status === "sent"
                    ? "rgba(52,211,153,0.10)"
                    : status === "today"
                    ? "rgba(34,211,238,0.12)"
                    : "rgba(127,127,127,0.10)";
                return (
                  <div key={`${item.type}-${item.id}`} className={classes.upcomingRow}>
                    <div className={classes.upcomingDate}>
                      <div
                        className={`${classes.upcomingDay} ${
                          status === "today" ? classes.upcomingDayToday : ""
                        }`}
                      >
                        {day}
                      </div>
                      <div className={classes.upcomingMonth}>{month}</div>
                    </div>
                    <span
                      className={classes.upcomingAvatar}
                      style={{ backgroundColor: avatarColor(item.id) }}
                    >
                      {initials(item.name)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={classes.upcomingName} title={item.name}>
                        {item.name}
                      </div>
                      <div className={classes.upcomingMeta}>
                        {item.type === "user" ? <BadgeIcon /> : <ContactsIcon />}
                        {i18n.t(`aeros.birthday.type.${item.type}`)}
                        {item.age || item.age === 0 ? ` · ${ageLabel(item.age)}` : ""}
                      </div>
                    </div>
                    <span
                      className={classes.upcomingStatus}
                      style={{ color: tone, backgroundColor: toneBg }}
                    >
                      {i18n.t(`aeros.birthday.status.${status}`)}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {stats && (
            <div className={classes.kpiGrid}>
              <div className={classes.kpiCard}>
                <div className={classes.kpiValue}>
                  {Number(stats.contactsWithDate || 0).toLocaleString()}
                </div>
                <div className={classes.kpiLabel}>
                  {i18n.t("aeros.birthday.kpi.contactsWithDate")}
                </div>
              </div>
              <div className={classes.kpiCard}>
                <div
                  className={classes.kpiValue}
                  style={{ color: stateText(theme, AEROS.success, "success") }}
                >
                  {Number(stats.sentThisYear || 0).toLocaleString()}
                </div>
                <div className={classes.kpiLabel}>
                  {i18n.t("aeros.birthday.kpi.sentThisYear", { year: stats.year })}
                </div>
              </div>
              <div className={classes.kpiCard}>
                <div
                  className={classes.kpiValue}
                  style={{ color: stateText(theme, AEROS.cyan, "info") }}
                >
                  {`${Number(stats.responseRate || 0)}%`}
                </div>
                <div className={classes.kpiLabel}>
                  {i18n.t("aeros.birthday.kpi.responded")}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TestMessageModal
        open={testOpen}
        onClose={() => setTestOpen(false)}
        message={settings?.contactBirthdayMessage}
        connectionName={selectedWhatsapp?.name}
      />
    </>
  );
};

export default BirthdaySettings;
